import { TweenLite } from "gsap"
import PropTypes from "prop-types"
import React, {
  memo,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
} from "react"
import styled from "styled-components"

import { ScrollerContext } from "../elements/SmoothScroll/context"

const Container = styled.div`
  padding: var(--gutter-l) var(--gutter-s);
  .title {
    position: absolute;
    z-index: 1;
    mix-blend-mode: difference;
    pointer-events: none;
    h1 {
      font-size: 6rem;
      font-family: "AVALANCHE";
      line-height: 0.85;
    }
  }
  &.january {
    color: var(--teal);
  }
  &.february {
    color: var(--february-text);
  }
  &.march {
    color: var(--march-text);
  }
  &.april {
    color: var(--april-text);
  }
  &.may {
    color: var(--may-text);
  }
  &.june {
    color: var(--june-text);
  }
  &.july {
    color: var(--july-text);
  }
  &.august {
    color: var(--august-text);
  }
  &.september {
    color: var(--september-text);
  }
  &.october {
    color: var(--october-text);
  }
  &.november {
    color: var(--november-text);
  }
  &.december {
    color: var(--december-text);
  }
  @media (min-width: 768px) {
    .title {
      h1 {
        font-size: 6rem;
      }
    }
  }
  @media (min-width: 1024px) {
    .title {
      h1 {
        font-size: 8rem;
      }
    }
  }
  @media (min-width: 1200px) {
    .title {
      h1 {
        font-size: 18rem;
      }
    }
  }
`

const Month = ({ name, children, ...props }) => {
  const context = useContext(ScrollerContext)

  const frameId = useRef(null)
  const containerEl = useRef(null)
  const titleEl = useRef(null)

  const onScroll = useCallback(() => {
    let target = containerEl.current.getBoundingClientRect().top
    let upperbound = window.innerHeight
    let lowerbound = containerEl.current.getBoundingClientRect().height
    const body = document.getElementsByTagName("BODY")[0]

    /* update background colour if month within active viewport */
    if (target < upperbound / 2 && target > 0 - lowerbound) {
      if (!body.classList.contains(`${name}`)) {
        body.classList.add(`${name}`)
      }
    } else {
      if (body.classList.contains(`${name}`)) {
        body.classList.remove(`${name}`)
      }
    }

    /* animate title if month within active viewport */
    if (target < 0 && target > 0 - lowerbound) {
      let yMove = target * (4 / 10) // recommend keeping this at 5 or below
      TweenLite.to(titleEl.current, 0.16, {
        y: -yMove,
      })
    }

    /* stop onScroll event once smoothScroll has finished */
    if (frameId.current) {
      frameId.current = window.requestAnimationFrame(onScroll)
    }
  }, [name])

  const renderTitle = () => {
    return name.split("").map((letter, i) => {
      if (i % 3 === 3 - 1) {
        return (
          <span key={i}>
            {letter} <br />
          </span>
        )
      }
      return <span key={i}>{letter}</span>
    })
  }

  useLayoutEffect(() => {
    if (context.isScrolling || !frameId.current) {
      frameId.current = window.requestAnimationFrame(onScroll)
    } else {
      window.cancelAnimationFrame(frameId.current)
    }
  }, [context.isScrolling, onScroll])

  return (
    <Container className={name} ref={containerEl} {...props}>
      <div className="title" ref={titleEl}>
        <h1>{renderTitle()}</h1>
      </div>
      {children}
    </Container>
  )
}

Month.propTypes = {
  name: PropTypes.string,
  children: PropTypes.node,
}

export default memo(Month)

/* 
  Notes:
  1.
  investigate memos to see if performance regarding this componenet can be improved
  by avoiding unnecessary renders
  2.
  investigate useEffectLayout and its dependency chain for this
  3.
  prevent title from re-rendering

*/
